/**
 * Unified Match Flow Layout Types
 */

import type { FlowContext, FlowPlayer, MatchFlowState, NodeState } from "@rivaled/shared";

/**
 * Data source abstraction for match flow UI.
 * Implemented by useMatchFlow (sockets), useTestMatchFlow (tRPC),
 * and useMockFlow (client-side mocks).
 */
export type MatchFlowDataSource = {
  flowState: MatchFlowState | null;
  currentNode: NodeState | null;
  isLoading: boolean;
  error: string | null;
  timeRemaining: number | null;
  progress: { completed: number; total: number };

  submitAction: (
    actionType: string,
    payload: unknown,
  ) => Promise<{ success: boolean; error?: string }>;

  // Extended actions (test/dev only)
  submitActionAs?: (
    playerId: string,
    actionType: string,
    payload: unknown,
  ) => Promise<{ success: boolean; error?: string }>;
  submitActionForAll?: (
    actionType: string,
    payload:
      | string
      | number
      | boolean
      | null
      | Record<string, unknown>
      | unknown[]
      | ((
          playerId: string,
        ) => string | number | boolean | null | Record<string, unknown> | unknown[]),
  ) => Promise<void>;
  triggerTimeout?: () => Promise<void>;
  refresh?: () => void;
};

/**
 * Resolved team data for layout rendering.
 */
export type TeamData = {
  teamIndex: number;
  teamName: string;
  players: FlowPlayer[];
  isMyTeam: boolean;
};

/**
 * Extracted team info from flow context.
 */
export type ResolvedTeams = {
  hasTeams: boolean;
  teamCount: number;
  teams: TeamData[];
  unassignedPlayers: FlowPlayer[];
};

/**
 * Resolve team assignments from flow state.
 */
export function resolveTeams(
  players: FlowPlayer[],
  context: FlowContext,
  currentPlayerId: string,
  teamNames: string[],
): ResolvedTeams {
  const teamMap = context.teams as Record<string, number> | undefined;
  const hasTeams = !!teamMap && Object.keys(teamMap).length > 0;

  if (!hasTeams) {
    return {
      hasTeams: false,
      teamCount: 0,
      teams: [],
      unassignedPlayers: players,
    };
  }

  const groups = new Map<number, FlowPlayer[]>();
  const unassigned: FlowPlayer[] = [];

  for (const player of players) {
    const teamNum = teamMap[player.userId];
    if (teamNum === undefined) {
      unassigned.push(player);
    } else {
      if (!groups.has(teamNum)) groups.set(teamNum, []);
      groups.get(teamNum)?.push(player);
    }
  }

  const teams: TeamData[] = [...groups.entries()]
    .sort(([a], [b]) => a - b)
    .map(([teamIndex, teamPlayers]) => ({
      teamIndex,
      teamName:
        teamPlayers.length === 1
          ? teamPlayers[0].username
          : (teamNames[teamIndex] ?? `Team ${teamIndex}`),
      players: teamPlayers,
      isMyTeam: teamMap[currentPlayerId] === teamIndex,
    }));

  return {
    hasTeams: true,
    teamCount: teams.length,
    teams,
    unassignedPlayers: unassigned,
  };
}
